import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { NotificationLevel, ScriptNotification } from '../model/script-notification.model';
import { TranslateService } from './translate.service';

/** Keys an error entry may carry its human-readable text under. */
const MESSAGE_KEYS = ['message', 'error', 'detail', 'defaultMessage'] as const;

/** Keys identifying which row of an import an error belongs to. */
const ROW_KEYS = ['row', 'rowNumber', 'line', 'lineNumber'] as const;

function firstString(entry: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = entry[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number') {
      return String(value);
    }
  }
  return null;
}

/**
 * Turns one error entry into a readable line, prefixed with the row and field
 * it applies to when the server names them — so a failed import reads as
 * "Row 3 - name: must not be empty" rather than a bare validation message.
 * The message itself is whatever the server sent and is not translated.
 */
function formatEntry(entry: unknown, rowLabel: string): string | null {
  if (typeof entry === 'string') {
    return entry.trim() || null;
  }
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const record = entry as Record<string, unknown>;
  const message = firstString(record, MESSAGE_KEYS);
  if (!message) {
    return null;
  }

  const row = firstString(record, ROW_KEYS);
  const field = firstString(record, ['field', 'property']);
  const prefix = [row ? `${rowLabel} ${row}` : null, field].filter(Boolean).join(' - ');

  return prefix ? `${prefix}: ${message}` : message;
}

/**
 * Every message the server sent with a failed request, one per entry when the
 * body reports failures per row. Returns an empty array when the response
 * carries nothing usable (a network failure, an HTML error page, an empty body).
 */
export function serverErrorMessages(error: unknown, rowLabel = 'Row'): string[] {
  const body = error instanceof HttpErrorResponse ? error.error : error;

  // A thrown Error or a failed ProgressEvent carries a technical message
  // ("Http failure response for ..."), not something to show a user. Only a
  // response body counts as a server message.
  if (body instanceof Error || (typeof Event !== 'undefined' && body instanceof Event)) {
    return [];
  }

  const format = (entry: unknown) => formatEntry(entry, rowLabel);

  if (Array.isArray(body)) {
    return body.map(format).filter((message): message is string => !!message);
  }

  if (body && typeof body === 'object') {
    const nested = (body as Record<string, unknown>)['errors'] ?? (body as Record<string, unknown>)['rows'];
    if (Array.isArray(nested)) {
      const messages = nested.map(format).filter((message): message is string => !!message);
      if (messages.length) {
        return messages;
      }
    }
  }

  const single = format(body);
  return single ? [single] : [];
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notificationSubject = new BehaviorSubject<ScriptNotification | null>(null);

  constructor(private translate: TranslateService) {}

  getNotification(): Observable<ScriptNotification | null> {
    return this.notificationSubject.asObservable();
  }

  show(notification: ScriptNotification): void {
    this.notificationSubject.next(notification);
  }

  /**
   * Shows what the server said about a failed request, one line per failed row.
   * `fallbackKey` is a translation key used when the response held no usable
   * message; the server's own text is shown as-is, in whatever language the
   * API produced it.
   */
  showServerError(error: unknown, fallbackKey: string, level: NotificationLevel = 'ERROR'): void {
    const messages = serverErrorMessages(error, this.translate.get('notification.row'));
    this.show({
      message: messages.length ? messages.join('\n') : this.translate.get(fallbackKey),
      level,
    });
  }

  dismiss(): void {
    this.notificationSubject.next(null);
  }
}
